import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * DCR: re-sync the cart after the shopper comes BACK to a page, typically from
 * checkout.
 *
 * The cart drawer and the header count are server-rendered into the page's HTML.
 * Items added afterwards go in over AJAX, so that HTML still describes the cart
 * as it was when the page first loaded (often empty). Going back re-uses it:
 *
 * - from the back/forward cache the DOM is restored as it was left, and only the
 *   cart icon corrects itself (and only within 10 seconds of the last change);
 * - without the cache the browser re-uses the stored HTML for the page, so the
 *   drawer and the count are whatever the first load rendered.
 *
 * Either way the shopper saw an empty cart until they refreshed. This asks the
 * server for the real cart and announces it with the same event an add-to-cart
 * dispatches, so the drawer, the count and anything else listening re-render as
 * they normally would.
 *
 * Both requests are `no-store`. On a history navigation the browser prefers cached
 * responses even for fetches made by the page's own script, so without it the
 * drawer's section came back from an earlier visit's cache and showed lines (a
 * gift row) that were no longer in the cart. The sections are fetched here and
 * attached to the event rather than left to the drawer's own fallback render,
 * which does not ask for a fresh copy.
 */

const navigationType = () => performance.getEntriesByType('navigation')[0]?.type;

let syncing = false;

async function syncCart() {
  if (syncing) return;
  syncing = true;

  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [],
      promise: deferred.promise,
    })
  );

  try {
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter((id) => Boolean(id));

    const [cart, sections] = await Promise.all([
      fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      }).then((response) => response.json()),
      sectionIds.length
        ? fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            cache: 'no-store',
          }).then((response) => response.json())
        : Promise.resolve(undefined),
    ]);

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'dcr-cart-restore', sections, didError: false },
    });
  } catch (error) {
    // Reject rather than leave listeners waiting on a promise that never settles.
    deferred.reject(error);
  } finally {
    syncing = false;
  }
}

// Restored from the back/forward cache: the page and this module were already
// running, and `pageshow` fires again with persisted = true.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) syncCart();
});

// Loaded again from stored HTML: `pageshow` fired before this module ran, so look
// at how we got here instead.
if (navigationType() === 'back_forward') syncCart();
